import { useState, useCallback, useRef, useMemo, useEffect, useLayoutEffect } from 'react'
import { useResizeObserver } from '@fs/zion-frontend-friends'

const DATA_NAV_ITEM_SELECTOR = '[data-nav-item]'

/**
 * Throttle function calls to animation frames to keep UI responsive during continuous resize/drag.
 * @param {Function} func - Function to throttle
 * @returns {Function} Throttled function
 */
function rafThrottle(func) {
  let rafId = null

  return function throttledFn(...args) {
    if (rafId != null) return

    rafId = window.requestAnimationFrame(() => {
      rafId = null
      func(...args)
    })
  }
}

function getElementWidth(el) {
  return el?.getBoundingClientRect().width || 0
}

/**
 * Read the horizontal gap applied to a flex container.
 * @param {HTMLElement} el
 * @returns {number} gap in px
 */
function getGapPx(el) {
  const styles = window.getComputedStyle(el)
  const gapStr = styles.gap || styles.columnGap || '0'
  return Number.parseFloat(gapStr) || 0
}

/**
 * Compute the index of the last item that can be visible, reserving space for the More button
 * and applying a small cushion (shrink one extra item) when overflow exists.
 * @param {HTMLElement} containerEl
 * @param {number} availableWidth
 * @param {number} moreWidth
 * @param {number} gapPx
 * @returns {number} last visible index (or -1 if none)
 */
function computeLastVisibleIndex(containerEl, availableWidth, moreWidth, gapPx) {
  const itemEls = Array.from(containerEl.querySelectorAll(DATA_NAV_ITEM_SELECTOR))
  const containerRect = containerEl.getBoundingClientRect()

  let fitIndex = -1

  for (let i = 0; i < itemEls.length; i++) {
    const reserve = i < itemEls.length - 1 ? moreWidth + gapPx : 0
    const itemRect = itemEls[i].getBoundingClientRect()
    const itemRight = itemRect.right - containerRect.left
    const willFit = itemRight + reserve <= availableWidth

    if (willFit) {
      fitIndex = i
    } else {
      break
    }
  }

  // Shrink one extra item to leave cushion next to the More button.
  const total = itemEls.length
  if (fitIndex >= 0 && fitIndex < total - 1) {
    fitIndex = Math.max(-1, fitIndex - 1)
  }

  return fitIndex
}

/**
 * Hook to manage responsive overflow behavior for navigation items.
 * Calculates which items can fit in the available width and which should be hidden in a More menu.
 *
 * @param {Array} content - Array of navigation items
 * @param {string} rowCalculatingCss - CSS class to apply during calculation
 * @param {Object} moreButtonRef - Ref for the rendered More button when visible
 * @returns {Object} - Object containing refs and state for responsive overflow
 */
export default function useResponsiveOverflow(content, rowCalculatingCss, moreButtonRef) {
  const itemsContainerRef = useRef(null)
  const [lastVisibleIndex, setLastVisibleIndex] = useState(-1)

  const calculateVisibleItems = useCallback(() => {
    const containerEl = itemsContainerRef.current
    if (!containerEl || !content?.length) return

    containerEl.classList.add(rowCalculatingCss)

    try {
      const available = getElementWidth(containerEl)
      const moreWidth = getElementWidth(moreButtonRef?.current)
      const gapPx = getGapPx(containerEl)

      const fitIndex = computeLastVisibleIndex(containerEl, available, moreWidth, gapPx)

      setLastVisibleIndex((currentIndex) => (currentIndex === fitIndex ? currentIndex : fitIndex))
    } finally {
      containerEl.classList.remove(rowCalculatingCss)
    }
  }, [content, moreButtonRef, rowCalculatingCss])

  const throttledCalc = useMemo(() => rafThrottle(calculateVisibleItems), [calculateVisibleItems])
  useResizeObserver(itemsContainerRef, throttledCalc)

  useEffect(() => {
    window.addEventListener('resize', throttledCalc)
    window.addEventListener('orientationchange', throttledCalc)

    return () => {
      window.removeEventListener('resize', throttledCalc)
      window.removeEventListener('orientationchange', throttledCalc)
    }
  }, [throttledCalc])

  useLayoutEffect(() => {
    if (content?.length) {
      calculateVisibleItems()
      return undefined
    }

    setLastVisibleIndex(-1)
    return undefined
  }, [content?.length, calculateVisibleItems])

  return {
    itemsContainerRef,
    lastVisibleIndex,
    recalculateVisibleItems: calculateVisibleItems,
  }
}
